"use client";
import { Play, Quote, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import type { VideoTestimonial } from '@/types/videoTestimonials';
import { Link } from 'react-router-dom';

function toEmbedUrl(url: string): string {
  if (url.includes('/embed/')) return url;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  return url;
}

interface VideoTestimonialsDisplayProps {
  testimonials: VideoTestimonial[];
}

// Pure presentational testimonials showcase — no data fetching, no Supabase
// import. Callers that already have their data (e.g. the homepage, which
// only needs the static fallback set) should use this directly instead of
// `VideoTestimonials`, so importing it doesn't pull @supabase/supabase-js
// into their bundle just to render a few video cards.
export function VideoTestimonialsDisplay({ testimonials: displayTestimonials }: VideoTestimonialsDisplayProps) {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [featuredIndex, setFeaturedIndex] = useState(0);

  const handlePlayVideo = (videoId: string) => {
    setActiveVideo(videoId);
  };

  const handleSelectFeatured = (originalIndex: number) => {
    setFeaturedIndex(originalIndex);
    setActiveVideo(displayTestimonials[originalIndex].id);
  };

  if (displayTestimonials.length === 0) {
    return null;
  }

  const featured = displayTestimonials[featuredIndex];
  const sideCards = displayTestimonials
    .map((t, i) => ({ testimonial: t, originalIndex: i }))
    .filter((_, i) => i !== featuredIndex)
    .slice(0, 3);

  return (
    <section className="py-16 bg-background overflow-x-hidden">
      <div className="container mx-auto max-w-7xl px-4 w-full">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 gap-1.5">
            <Quote className="w-3.5 h-3.5" />
            Video Testimonials
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Hear From Our Customers
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Real stories from HR leaders who transformed their recruitment with Hirebee
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 min-w-0">
          {/* Featured large card - left */}
          <div className="lg:col-span-3 min-w-0">
            <Card className="overflow-hidden hover:shadow-lg transition-shadow group h-full">
              <div className="h-full flex flex-col">
                <div className="relative aspect-video bg-muted overflow-hidden">
                  {activeVideo === featured.id ? (
                    <iframe
                      src={`${toEmbedUrl(featured.video_url)}?autoplay=1&rel=0&modestbranding=1&showinfo=0&playlist=zJL0gfXrykc,fIQeH0MqpSM,4iYIc3nr4hc,s8nL9CtH-xM`}
                      title={`${featured.customer_name} testimonial`}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        src={featured.thumbnail_url}
                        alt={`${featured.customer_name} from ${featured.company_name}`}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <button
                          onClick={() => handlePlayVideo(featured.id)}
                          className="w-20 h-20 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all hover:scale-110"
                          aria-label={`Play video testimonial from ${featured.customer_name}`}
                        >
                          <Play className="w-8 h-8 text-primary ml-1" />
                        </button>
                      </div>
                      {featured.duration && (
                        <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded bg-black/70 text-white text-sm font-medium">
                          {featured.duration}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="p-4 flex flex-col justify-center">
                  <p className="text-base italic text-muted-foreground mb-3">
                    "{featured.quote}"
                  </p>
                  <div>
                    <p className="font-semibold text-base">{featured.customer_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {featured.customer_title}, {featured.company_name}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Two smaller cards - right */}
          <div className="lg:col-span-2 flex flex-col gap-3 min-w-0">
            {sideCards.map(({ testimonial, originalIndex }) => (
              <Card
                key={testimonial.id}
                onClick={() => handleSelectFeatured(originalIndex)}
                className="overflow-hidden hover:shadow-md transition-shadow group cursor-pointer ring-0 hover:ring-2 hover:ring-primary/40 transition-all border-l-2 border-l-primary/20 flex-1"
              >
                <div className="flex flex-row h-full">
                  <div className="relative w-[45%] flex-shrink-0 bg-muted overflow-hidden min-h-[120px]">
                    <img
                      src={testimonial.thumbnail_url}
                      alt={`${testimonial.customer_name} from ${testimonial.company_name}`}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center transition-all group-hover:scale-110">
                        <Play className="w-3.5 h-3.5 text-primary ml-0.5" />
                      </div>
                    </div>
                    {testimonial.duration && (
                      <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-medium">
                        {testimonial.duration}
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-center min-w-0 overflow-hidden">
                    <p className="text-sm italic text-muted-foreground mb-2 line-clamp-2">
                      "{testimonial.quote}"
                    </p>
                    <p className="font-semibold text-sm truncate">{testimonial.customer_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {testimonial.customer_title}, {testimonial.company_name}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Button asChild size="lg" className="h-12 px-8 text-base font-bold uppercase tracking-wide bg-gradient-primary text-white hover:opacity-90 shadow-lg border-0">
            <Link to="/customer-stories">
              View All Customer Stories
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
